import { DGBillingRecordsService, DGConsumptionService } from '../../services/realApi';
import { VALIDATION_LIMITS } from '../../config/validationConfig';

// Helper functions
const validateDGBillingRecord = (recordData) => {
  const errors = [];

  if (!recordData) {
    throw new Error('Record data is required');
  }

  if (
    !recordData.billingPeriod
    || !recordData.billingPeriod.startDate
    || !recordData.billingPeriod.endDate
  ) {
    errors.push('Billing period with start and end dates is required');
  }

  if (!recordData.dieselConsumption || recordData.dieselConsumption <= 0) {
    errors.push('Diesel consumption must be greater than zero');
  }

  if (!recordData.runningHours || recordData.runningHours <= 0) {
    errors.push('Running hours must be greater than zero');
  }

  if (recordData.dieselConsumption > VALIDATION_LIMITS.DG.DIESEL_CONSUMPTION.MAX) {
    errors.push(
      `Diesel consumption seems unusually high (max ${
        VALIDATION_LIMITS.DG.DIESEL_CONSUMPTION.MAX.toLocaleString()
      } L)`,
    );
  }

  if (recordData.runningHours > VALIDATION_LIMITS.DG.RUNNING_HOURS.MAX) {
    errors.push(
      `Running hours cannot exceed ${
        VALIDATION_LIMITS.DG.RUNNING_HOURS.MAX.toLocaleString()
      } hours per year`,
    );
  }

  if (errors.length > 0) {
    throw new Error(`Validation failed: ${errors.join(', ')}`);
  }
};

const getErrorMessage = (error, defaultMessage) => {
  if (!error) return defaultMessage;

  // Network errors
  if (error.message && error.message.includes('fetch')) {
    return 'Network connection failed. Please check your internet connection and try again.';
  }

  // HTTP errors
  if (error.message && error.message.includes('HTTP 404')) {
    return 'The requested resource was not found. It may have been deleted or moved.';
  }

  if (error.message && error.message.includes('HTTP 403')) {
    return 'You do not have permission to perform this action. Please contact your administrator.';
  }

  if (error.message && error.message.includes('HTTP 500')) {
    return 'Server error occurred. Please try again later or contact support if the problem persists.';
  }

  if (error.message && error.message.includes('HTTP 400')) {
    return 'Invalid request data. Please check your input and try again.';
  }

  // Timeout errors
  if (error.message && (error.message.includes('timeout') || error.message.includes('TIMEOUT'))) {
    return 'Request timed out. Please check your connection and try again.';
  }

  // Validation errors (pass through as they're already user-friendly)
  if (error.message && error.message.includes('Validation failed')) {
    return error.message;
  }

  // Use the error message if it's user-friendly, otherwise use default
  if (error.message && error.message.length < 100 && !error.message.includes('Error:')) {
    return error.message;
  }

  return defaultMessage;
};

const initialState = {
  dgBillingRecords: [],
  currentDGBill: null,
  dgConsumptionData: null,
  loading: false,
  error: null,
};

const mutations = {
  SET_DG_BILLING_RECORDS(state, records) {
    state.dgBillingRecords = records;
  },
  SET_CURRENT_DG_BILL(state, bill) {
    state.currentDGBill = bill;
  },
  SET_DG_CONSUMPTION_DATA(state, data) {
    state.dgConsumptionData = data;
  },
  ADD_DG_BILLING_RECORD(state, record) {
    state.dgBillingRecords.push(record);
  },
  UPDATE_DG_BILLING_RECORD(state, updatedRecord) {
    const index = state.dgBillingRecords.findIndex((record) => (
      record.id === updatedRecord.id || record._id === updatedRecord._id
    ));
    if (index !== -1) {
      state.dgBillingRecords.splice(index, 1, updatedRecord);
    }
  },
  REMOVE_DG_BILLING_RECORD(state, recordId) {
    const index = state.dgBillingRecords.findIndex((record) => (
      record.id === recordId || record._id === recordId
    ));
    if (index !== -1) {
      state.dgBillingRecords.splice(index, 1);
    }
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
};

const actions = {
  async fetchDGBillingRecords({ commit }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const records = await DGBillingRecordsService.getAllRecords();

      // Ensure we always have an array
      const recordsArray = Array.isArray(records) ? records : [];
      commit('SET_DG_BILLING_RECORDS', recordsArray);
      return recordsArray;
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Failed to load DG billing records');
      commit('SET_ERROR', errorMessage);

      // Return empty array on error to prevent overlap detection issues
      commit('SET_DG_BILLING_RECORDS', []);
      throw new Error(errorMessage);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchDGConsumptionData({ commit }, { startDate, endDate }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      // Validate date inputs
      if (!startDate || !endDate) {
        throw new Error('Start date and end date are required');
      }

      const start = new Date(startDate);
      const end = new Date(endDate);

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        throw new Error('Invalid date format provided');
      }

      if (start >= end) {
        throw new Error('Start date must be before end date');
      }

      if (end > new Date()) {
        throw new Error('End date cannot be in the future');
      }

      const data = await DGConsumptionService.getDGConsumptionData(startDate, endDate);
      commit('SET_DG_CONSUMPTION_DATA', data);
      return data;
    } catch (error) {
      console.error('Error fetching DG consumption data:', error);
      const errorMessage = getErrorMessage(error, 'Failed to retrieve DG consumption data');
      commit('SET_ERROR', errorMessage);
      throw new Error(errorMessage);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async saveDGBillingRecord({ commit }, recordData) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      // Validate record data before saving
      validateDGBillingRecord(recordData);

      const newRecord = await DGBillingRecordsService.createRecord(recordData);
      commit('ADD_DG_BILLING_RECORD', newRecord);
      return newRecord;
    } catch (error) {
      console.error('Error saving DG billing record:', error);
      const errorMessage = getErrorMessage(error, 'Failed to save DG billing record');
      commit('SET_ERROR', errorMessage);
      throw new Error(errorMessage);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateDGBillingRecord({ commit }, { id, recordData }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      if (!id) {
        throw new Error('Record ID is required for update');
      }

      // Validate record data before updating
      validateDGBillingRecord(recordData);

      const updatedRecord = await DGBillingRecordsService.updateRecord(id, recordData);
      commit('UPDATE_DG_BILLING_RECORD', updatedRecord);
      return updatedRecord;
    } catch (error) {
      console.error('Error updating DG billing record:', error);
      const errorMessage = getErrorMessage(error, 'Failed to update DG billing record');
      commit('SET_ERROR', errorMessage);
      throw new Error(errorMessage);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async deleteDGBillingRecord({ commit }, recordId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      if (!recordId) {
        throw new Error('Record ID is required for deletion');
      }

      await DGBillingRecordsService.deleteRecord(recordId);
      commit('REMOVE_DG_BILLING_RECORD', recordId);
      return true;
    } catch (error) {
      console.error('Error deleting DG billing record:', error);
      const errorMessage = getErrorMessage(error, 'Failed to delete DG billing record');
      commit('SET_ERROR', errorMessage);
      throw new Error(errorMessage);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchDGBillingRecordById({ commit }, recordId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      if (!recordId) {
        throw new Error('Record ID is required');
      }

      const record = await DGBillingRecordsService.getRecordById(recordId);
      if (!record) {
        throw new Error('DG billing record not found');
      }
      return record;
    } catch (error) {
      console.error('Error fetching DG billing record by ID:', error);
      const errorMessage = getErrorMessage(error, 'Failed to load DG billing record');
      commit('SET_ERROR', errorMessage);
      throw new Error(errorMessage);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchDGBillingRecordForEdit({ commit }, id) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    try {
      const record = await DGBillingRecordsService.getRecordById(id);
      if (record) {
        commit('SET_CURRENT_DG_BILL', record);
      }
      return record;
    } catch (error) {
      console.error('Error fetching DG billing record for edit:', error);
      const errorMessage = getErrorMessage(error, 'Failed to load DG billing record for editing');
      commit('SET_ERROR', errorMessage);
      throw new Error(errorMessage);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  setCurrentDGBill({ commit }, billData) {
    commit('SET_CURRENT_DG_BILL', billData);
  },

  clearCurrentDGBill({ commit }) {
    commit('SET_CURRENT_DG_BILL', null);
  },

  clearDGConsumptionData({ commit }) {
    commit('SET_DG_CONSUMPTION_DATA', null);
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR');
  },
};

const getters = {
  allDGBillingRecords: (state) => state.dgBillingRecords,
  currentDGBill: (state) => state.currentDGBill,
  dgConsumptionData: (state) => state.dgConsumptionData,
  loading: (state) => state.loading,
  error: (state) => state.error,

  getDGBillingRecordById: (state) => (id) => state.dgBillingRecords.find((record) => (
    record.id === id || record._id === id
  )),

  isEditMode: (state) => state.currentDGBill && (state.currentDGBill.id || state.currentDGBill._id),

  getDGBillingRecordForEdit: (state) => (id) => state.dgBillingRecords.find((record) => (
    record.id === id || record._id === id
  )),

  // Filter DG billing records by date range
  getDGBillingRecordsByDateRange: (state) => (startDate, endDate) => (
    state.dgBillingRecords.filter((record) => {
      const recordStart = new Date(record.billingPeriod.startDate);
      const recordEnd = new Date(record.billingPeriod.endDate);
      const filterStart = new Date(startDate);
      const filterEnd = new Date(endDate);

      return recordStart >= filterStart && recordEnd <= filterEnd;
    })
  ),

  // Filter DG billing records by efficiency metrics
  getDGBillingRecordsByEfficiency: (
    state,
  ) => (minKwhPerLitre = 0, maxKwhPerLitre = Infinity) => (
    state.dgBillingRecords.filter((record) => {
      const efficiency = record.efficiencyMetrics?.kwhPerLitre || 0;
      return efficiency >= minKwhPerLitre && efficiency <= maxKwhPerLitre;
    })
  ),

  // Get DG billing records sorted by date (newest first)
  getDGBillingRecordsSortedByDate: (state) => [...state.dgBillingRecords].sort((a, b) => {
    const dateA = new Date(a.billingPeriod.startDate);
    const dateB = new Date(b.billingPeriod.startDate);
    return dateB - dateA; // Newest first
  }),

  // Search DG billing records by text
  searchDGBillingRecords: (state) => (searchTerm) => {
    if (!searchTerm) return state.dgBillingRecords;

    const term = searchTerm.toLowerCase();
    return state.dgBillingRecords.filter((record) => (
      record.createdBy?.toLowerCase().includes(term)
        || record.billingPeriod.startDate.toLowerCase().includes(term)
        || record.billingPeriod.endDate.toLowerCase().includes(term)
        || record.dieselConsumption?.toString().includes(term)
        || record.runningHours?.toString().includes(term)
    ));
  },

  // Get total statistics for all DG billing records
  getDGBillingStatistics: (state) => {
    const records = state.dgBillingRecords;
    if (records.length === 0) {
      return {
        totalRecords: 0,
        totalDGConsumption: 0,
        totalDieselConsumption: 0,
        totalRunningHours: 0,
        averageKwhPerLitre: 0,
        averageLitresPerHour: 0,
      };
    }

    const totals = records.reduce((acc, record) => {
      acc.totalDGConsumption += record.dgConsumptionData?.totalConsumption || 0;
      acc.totalDieselConsumption += record.dieselConsumption || 0;
      acc.totalRunningHours += record.runningHours || 0;
      acc.totalKwhPerLitre += record.efficiencyMetrics?.kwhPerLitre || 0;
      acc.totalLitresPerHour += record.efficiencyMetrics?.litresPerHour || 0;
      return acc;
    }, {
      totalDGConsumption: 0,
      totalDieselConsumption: 0,
      totalRunningHours: 0,
      totalKwhPerLitre: 0,
      totalLitresPerHour: 0,
    });

    return {
      totalRecords: records.length,
      totalDGConsumption: totals.totalDGConsumption,
      totalDieselConsumption: totals.totalDieselConsumption,
      totalRunningHours: totals.totalRunningHours,
      averageKwhPerLitre: totals.totalKwhPerLitre / records.length,
      averageLitresPerHour: totals.totalLitresPerHour / records.length,
    };
  },
};

export default {
  namespaced: true,
  state: initialState,
  mutations,
  actions,
  getters,
};
